import { Module } from 'vuex'
import { CartState, CartItem, Product } from '@/types'

const STORAGE_KEY = 'jh_cart'

function loadCart (): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function saveCart (items: CartItem[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
}

const cart: Module<CartState, unknown> = {
  namespaced: true,

  state: (): CartState => ({
    items: loadCart(),
    isOpen: false
  }),

  getters: {
    itemCount: (state) => state.items.reduce((sum, i) => sum + i.quantity, 0),
    subtotal: (state) => state.items.reduce((sum, i) => sum + i.product.price * i.quantity, 0),
    isEmpty: (state) => state.items.length === 0
  },

  mutations: {
    SET_OPEN (state, open: boolean) { state.isOpen = open },

    ADD_ITEM (state, { product, quantity }: { product: Product; quantity: number }) {
      const existing = state.items.find(i => i.product.id === product.id)
      if (existing) {
        existing.quantity += quantity
      } else {
        state.items.push({ product, quantity })
      }
      saveCart(state.items)
    },

    UPDATE_QUANTITY (state, { productId, quantity }: { productId: string; quantity: number }) {
      const item = state.items.find(i => i.product.id === productId)
      if (item) {
        item.quantity = quantity
        saveCart(state.items)
      }
    },

    REMOVE_ITEM (state, productId: string) {
      state.items = state.items.filter(i => i.product.id !== productId)
      saveCart(state.items)
    },

    CLEAR_CART (state) {
      state.items = []
      localStorage.removeItem(STORAGE_KEY)
    }
  },

  actions: {
    addToCart ({ commit }, payload: { product: Product; quantity?: number }) {
      commit('ADD_ITEM', { product: payload.product, quantity: payload.quantity ?? 1 })
      commit('SET_OPEN', true)
    },
    updateQuantity ({ commit }, payload: { productId: string; quantity: number }) {
      if (payload.quantity < 1) {
        commit('REMOVE_ITEM', payload.productId)
      } else {
        commit('UPDATE_QUANTITY', payload)
      }
    },
    removeItem ({ commit }, productId: string) {
      commit('REMOVE_ITEM', productId)
    },
    clearCart ({ commit }) {
      commit('CLEAR_CART')
    },
    toggleCart ({ commit, state }) {
      commit('SET_OPEN', !state.isOpen)
    }
  }
}

export default cart
